import { useState, useEffect, useMemo, useRef } from 'react';
import apiClient from '../services/apiClient';

const BUDGET_FETCH_COOLDOWN_MS = 120000;

/**
 * Custom hook for managing budget analysis data via batch API.
 * Handles budget alerts and burn rate forecasts.
 * Gracefully handles permission errors for users without budget access.
 *
 * @param {Array<object>} projects - Project list from API.
 * @param {{ enabled?: boolean }} [options] - When enabled is false, skips fetch.
 */
export const useBudgetAnalysis = (projects, options = {}) => {
    const { enabled = true } = options;
    const [budgetAlerts, setBudgetAlerts] = useState({});
    const [burnRateForecasts, setBurnRateForecasts] = useState({});
    const [loading, setLoading] = useState(false);
    const [hasPermission, setHasPermission] = useState(true);
    const projectsRef = useRef([]);
    const lastFetchRef = useRef(null);

    const projectIds = useMemo(() => {
        return projects.map((p) => p.id).sort().join(',');
    }, [projects]);

    useEffect(() => {
        const currentIds = projects.map((p) => p.id).sort().join(',');
        const previousIds = projectsRef.current.map((p) => p.id).sort().join(',');

        if (currentIds !== previousIds) {
            projectsRef.current = projects;
        }
    }, [projects]);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        if (!projects.length || (lastFetchRef.current && Date.now() - lastFetchRef.current < BUDGET_FETCH_COOLDOWN_MS)) {
            return;
        }

        let cancelled = false;

        const fetchBudgetAnalysis = async () => {
            setLoading(true);
            const alertsObj = {};
            const forecastsObj = {};

            try {
                const ids = projects.map((project) => project.id).join(',');
                const response = await apiClient.get('/projects/budget-analysis-batch/', {
                    params: { project_ids: ids },
                });
                const results = response.data?.results || {};
                const projectById = Object.fromEntries(projects.map((project) => [String(project.id), project]));

                Object.entries(results).forEach(([projectId, data]) => {
                    const project = projectById[projectId];
                    if (!project) {
                        return;
                    }

                    alertsObj[project.id] = data.budget_alerts || [];

                    if (data.burn_rate) {
                        let forecastStatus = 'ok';
                        if (data.budget_forecast) {
                            if (data.budget_forecast.is_forecast_over_budget) {
                                forecastStatus = 'critical';
                            } else if (data.budget_forecast.forecasted_variance > 0) {
                                forecastStatus = 'warning';
                            }
                        } else if (project.status === 'planning' || !project.start_date) {
                            forecastStatus = 'warning';
                        }

                        forecastsObj[project.id] = {
                            burn_rate: data.burn_rate,
                            forecast_exhaustion_date: data.budget_forecast?.forecasted_completion_date,
                            forecast_status: forecastStatus,
                        };
                    } else {
                        forecastsObj[project.id] = null;
                    }
                });

                projects.forEach((project) => {
                    if (!(project.id in alertsObj)) {
                        alertsObj[project.id] = [];
                        forecastsObj[project.id] = null;
                    }
                });
            } catch (error) {
                if (error.response && error.response.status === 403) {
                    setHasPermission(false);
                    return;
                }
                console.error('Error fetching batch budget analysis:', error);
                projects.forEach((project) => {
                    alertsObj[project.id] = [];
                    forecastsObj[project.id] = null;
                });
            }

            if (!cancelled && hasPermission) {
                setBudgetAlerts(alertsObj);
                setBurnRateForecasts(forecastsObj);
            }
            if (!cancelled) {
                setLoading(false);
                lastFetchRef.current = Date.now();
            }
        };

        fetchBudgetAnalysis().catch((err) => {
            if (process.env.NODE_ENV === 'development') {
                console.error('Budget analysis batch top-level error:', err);
            }
            if (!cancelled) {
                setLoading(false);
            }
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectIds, enabled]);

    return {
        budgetAlerts,
        burnRateForecasts,
        loading,
        hasPermission,
    };
};
